import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
  Typed,
  TypedValue,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';

import {
  ContentTab,
  ContentType,
  ContentTypeName,
  ExtrasContent,
} from '../model/extras.domain';
import { useExtrasModel } from '../model/extras.model';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  defaultValues,
  emptyTab,
  filterExtrasVM,
  filterTypeOptions,
  getContentName,
  toggleIsPublished,
  updateTreeItems,
} from './extras.mappers';
import { Draggable } from './extras.types';
import { ExtrasFormVM, useExtrasFormVM } from './extrasForm.viewModel';
import { a } from 'vitest/dist/chunks/suite.d.FvehnV49';
import { v4 } from 'uuid';
import { useUser } from '@client/new/hooks/use-user';
import { reset } from 'mixpanel-browser';
import { mixTrack } from '@services/mixpanel';

export type Filters = {
  type: (typeof filterTypeOptions)[number]['value'] | '';
};

export type ExtrasVMActionMap = {
  Create: ContentTypeName;
  Edit: string;
  Delete: string;
  ToggleVisiblity: null;
  SaveTab: string;
  SetSearch: string;
  SetFilterType: Filters['type'];
  Reorder: Draggable[];
  ToggleItemVisible: string;
  CloseModal: null;
  CreateContent: ContentType;
  ConfirmDelete: null;
};

export type ExtrasVMAction = Actions<ExtrasVMActionMap>;

export type ExtrasVM = {
  search: string;
  filters: Filters;

  modalMode:
    | TypedValue<
        'edit' | 'create' | 'delete',
        ExtrasFormVM<'ContentLink' | 'ContentLegacyGuide' | 'ContentTab'>
      >
    | Typed<'none'>;
  formType: 'ContentLink' | 'ContentLegacyGuide' | 'ContentTab';
  currentId?: string;
  isExtrasEmpty: boolean;
} & Dispatchers<ExtrasVMActionMap> &
  ExtrasContent;

export const useExtrasVM = (): Async<ExtrasVM> => {
  const influencerId = useInfluencerId();
  const [extrasContent, setExtrasContent] = useState<ExtrasContent>({
    contentIds: {
      id: 'empty',
    },
    contentMap: {},
  });
  const model = useExtrasModel({
    creatorId: influencerId,
  });
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    type: '',
  });
  const { roleLite } = useUser();
  const [currentId, setCurrentId] = useState<string | undefined>(undefined);
  const [modalModeType, setModalModeType] = useState<
    'create' | 'edit' | 'none' | 'delete'
  >('none');
  const [formType, setFormType] = useState<ContentTypeName>('ContentLink');
  const mainTab = extrasContent.contentIds?.items?.[0];
  const topLevelItems = mainTab?.items ?? [];
  const isExtrasEmpty = topLevelItems.length === 0;
  const canDeleteExtras = roleLite !== 'user';

  const handleSubmit = (data: ContentType): Promise<unknown> => {
    if (model.data.type !== 'fetched')
      return Promise.reject(new Error('Model not fetched'));

    if (modalModeType === 'create') {
      const parentId = mainTab?.id || 'empty';

      return model.create(data, parentId).then(() => {
        mixTrack({
          type: 'Extras Block Created',
          payload: {
            type: formType,
            title: getContentName(data),
          },
        });
        setModalModeType('none');
        setFormType('ContentLink');
      });
    }

    if (modalModeType === 'edit' && currentId) {
      return model
        .update({
          type: data.type,
          value: {
            ...data.value,
            id: currentId,
          },
        } as ContentType)
        .then(() => {
          mixTrack({
            type: 'Extras Block Saved',
            payload: {
              type: formType,
              title: getContentName(data),
            },
          });
          setModalModeType('none');
          setFormType('ContentLink');
        });
    }

    return Promise.reject(new Error('Invalid modal mode or currentId'));
  };

  const extrasFormVM = useExtrasFormVM({ formType, model, handleSubmit });

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    if (model.data.value.contentIds.id === 'empty') {
      console.log('No contentIds found, creating default ContentTab');
      return dispatchers.dispatch('Create', 'ContentTab');
    }
  }, [model.data.type]);

  useEffect(() => {
    if (model.data.type !== 'fetched') return;
    const contentIds = model.data.value.contentIds;
    const filteredContentMap = filterExtrasVM(
      model.data.value,
      search,
      filters,
    );
    setExtrasContent(() => ({
      contentIds,
      contentMap: filteredContentMap,
    }));
  }, [model.data.type, search, filters]);

  const _dispatch = (a: ExtrasVMAction) => {
    if (model.data.type !== 'fetched') return;
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'SaveTab': {
        if (modalModeType === 'create') {
          model
            .create(emptyTab(a.payload), model.data.value.contentIds.id)
            .then(() => {
              setModalModeType('none');
              setFormType('ContentLink');
            });
        }
        if (modalModeType === 'edit' && currentId) {
          const contentTab = model.data.value.contentMap[currentId];

          if (!contentTab || contentTab.type !== 'ContentTab') return;

          const updatedContent: ContentTab = {
            ...contentTab,
            value: {
              ...contentTab.value,
              name: a.payload,
            },
          };

          model.update(updatedContent).then(() => {
            setModalModeType('none');
            setFormType('ContentLink');
          });
        }

        mixTrack({
          type: 'Extra Tab Named and Saved',
          payload: {
            'Tab Name': a.payload,
          },
        });
        return;
      }
      case 'SetSearch': {
        setSearch(a.payload);
        return;
      }
      case 'SetFilterType': {
        const newFilters = { ...filters, type: a.payload };
        setFilters(newFilters);
        return;
      }
      case 'Create': {
        setModalModeType('create');
        setFormType(a.payload);
        extrasFormVM.reset(defaultValues[a.payload]);

        return;
      }
      case 'CloseModal': {
        setModalModeType('none');
        setFormType('ContentLink');
        setCurrentId(undefined);
        return;
      }
      case 'Edit': {
        const contentId = a.payload;
        const content = model.data.value.contentMap[contentId];
        if (!content) return;

        if (content.type === 'ContentLegacyGuide' && roleLite === 'user')
          return;

        setModalModeType('edit');
        setFormType(content.type);
        setCurrentId(contentId);
        extrasFormVM.reset(content.value);

        return;
      }
      case 'ToggleItemVisible': {
        const contentId = a.payload;
        setCurrentId(contentId);
        const c = extrasContent.contentMap[contentId];

        const updatedContent = toggleIsPublished(c);

        model
          .update(updatedContent)
          .then(() => {
            mixTrack({
              type: 'Extras Block visibility toggled',
              payload: {
                type: c.type,
                title: getContentName(c),
                visibility: updatedContent.value.isPublished,
              },
            });
          })
          .catch((error) => {
            console.error('Visibility change failed:', error);
          });

        setExtrasContent((prev) => ({
          ...prev,
          contentMap: {
            ...prev.contentMap,
            [contentId]: toggleIsPublished(prev.contentMap[contentId]),
          },
        }));

        return;
      }
      case 'Reorder': {
        const updatedContentIds = updateTreeItems(
          model.data.value.contentIds,
          (extrasContent.contentIds.items || [])[0].id,

          a.payload.map((item) => ({
            id: item.id,
          })),
        );

        model.reorder(updatedContentIds).catch((error) => {
          console.error('Reorder failed:', error);
        });

        setExtrasContent((prev) => ({
          ...prev,
          contentIds: updatedContentIds,
        }));
        return;
      }
      case 'Delete': {
        setCurrentId(a.payload);
        setModalModeType('delete');
        return;
      }

      case 'ConfirmDelete': {
        if (!currentId) return;
        const c = extrasContent.contentMap[currentId];
        model
          .delete(c)
          .then(() => {
            mixTrack({
              type: 'Extras Block Deleted',
              payload: {
                type: c.type,
                title: getContentName(c),
              },
            });
            setModalModeType('none');
            setCurrentId(undefined);
          })
          .catch(() => {
            console.error('Delete failed');
          });

        return;
      }
    }
  };

  const dispatchers = getDispatchers<ExtrasVMActionMap>(_dispatch);

  if (model.data.type !== 'fetched' && extrasContent.contentIds.id === 'empty')
    return model.data;

  return toAsyncFetched({
    isExtrasEmpty,
    canDeleteExtras,
    search,
    filters,
    formType,
    currentId,
    modalMode:
      modalModeType === 'none'
        ? { type: 'none' }
        : {
            type: modalModeType,
            value: extrasFormVM,
          },
    ...dispatchers,
    ...extrasContent,
  }) as any; // todo: fix type casting;
};
